import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Snackbar,
  Alert,
  IconButton,
  TextareaAutosize,
  CircularProgress,
  Typography,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { getDecryptedAccessToken } from '../../../Utils/LoginUserDetails.js/LoginUserSavedDetails';
import ApiServices from '../../../Utils/ApiServices/ApiServices';

const Enquiries = () => {
  const accessToken = getDecryptedAccessToken();
  const [data, setData] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [showOnlyUncontacted, setShowOnlyUncontacted] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [noteLoading, setNoteLoading] = useState({});

  const fetchData = async () => {
    try {
      const res = await ApiServices.get('/apis/default/api/wellnessenquiry', {
        Authorization: `Bearer ${accessToken}`,
      });
      const sorted = res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setData(sorted);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to fetch data', severity: 'error' });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCheckboxChange = async (id, current) => {
    try {
      await ApiServices.put(`/apis/default/api/wellnessenquiry/${id}`, {
        contacted_by_receptionist: current === '1' ? '0' : '1',
      }, {
        Authorization: `Bearer ${accessToken}`,
      });
      setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
      fetchData();
    } catch {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const handleNoteSave = async (id, note) => {
    setNoteLoading((prev) => ({ ...prev, [id]: true }));
    try {
      await ApiServices.put(`/apis/default/api/wellnessenquiry/${id}`, {
        notes: note,
      }, {
        Authorization: `Bearer ${accessToken}`,
      });
      setSnackbar({ open: true, message: 'Note saved successfully', severity: 'success' });
      fetchData();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save note', severity: 'error' });
    } finally {
      setNoteLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleDelete = async (id) => {
    try {
      await ApiServices.delete(`/apis/default/api/wellnessenquiry/${id}`, {
        Authorization: `Bearer ${accessToken}`,
      });
      setSnackbar({ open: true, message: 'Deleted successfully', severity: 'success' });
      fetchData();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete', severity: 'error' });
    }
  };

  const filteredData = data.filter((entry) => {
    const nameMatch = `${entry.fname} ${entry.lname}`.toLowerCase().includes(filterName.toLowerCase());
    const emailMatch = entry.email.toLowerCase().includes(filterEmail.toLowerCase());
    const contactMatch = showOnlyUncontacted ? entry.contacted_by_receptionist === '0' : true;
    return nameMatch && emailMatch && contactMatch;
  });

  return (
    <Box p={4}>
      <Typography variant="h5" mb={2} color="#05b6b3">Wellness Enquiries</Typography>
      <Box mb={2} display="flex" gap={2} flexWrap="wrap">
        <TextField label="Search by Name" value={filterName} onChange={(e) => setFilterName(e.target.value)} />
        <TextField label="Search by Email" value={filterEmail} onChange={(e) => setFilterEmail(e.target.value)} />
        <Box display="flex" alignItems="center">
          <Checkbox
            checked={showOnlyUncontacted}
            onChange={(e) => setShowOnlyUncontacted(e.target.checked)}
          />
          <Typography>Show only uncontacted</Typography>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Contacted</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.fname} {row.lname}</TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.phone}</TableCell>
                <TableCell>{row.message}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={row.contacted_by_receptionist === '1'}
                    onChange={() => handleCheckboxChange(row.id, row.contacted_by_receptionist)}
                  />
                </TableCell>
                <TableCell>
                  <TextareaAutosize
                    minRows={3}
                    placeholder="Enter note"
                    style={{ width: '100%' }}
                    defaultValue={row.notes || ''}
                    onBlur={(e) => handleNoteSave(row.id, e.target.value)}
                    disabled={noteLoading[row.id]}
                  />
                  {noteLoading[row.id] && <CircularProgress size={16} />}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleDelete(row.id)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Enquiries;
